import type { ModalFiltersConfigSection } from '@rocco/ui/filter-sort/types/filter-config-section';

/** A theme as the listing's facets need it. */
export interface ThemeFacetOption {
    title: string;
    segment: string;
    dimension: string;
}

/**
 * URL parameters carrying theme selections are prefixed rather than named for
 * their dimension alone.
 *
 * Dimensions are editorial data, so a new one called "Order" or "Page" would
 * otherwise collide with a parameter this listing already uses — and the
 * collision would show up as a filter that silently stops working, not as an
 * error. The prefix also lets the loader recognise a theme parameter without
 * knowing the vocabulary.
 */
export const THEME_PARAM_PREFIX = 'theme-';

const slug = (value: string): string =>
    value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

/** The URL parameter a dimension's group writes to, e.g. `theme-material`. */
export const themeParamFor = (dimension: string): string =>
    `${THEME_PARAM_PREFIX}${slug(dimension)}`;

/** Every theme parameter present on a URL, in the order the vocabulary defines. */
export const themeParams = (searchParams: URLSearchParams): string[] =>
    [...searchParams.keys()].filter((key) =>
        key.startsWith(THEME_PARAM_PREFIX),
    );

/**
 * Every theme segment selected across all dimensions, de-duplicated.
 *
 * Flattened deliberately: the server-side filter regroups them by dimension, so
 * the client does not have to preserve the split. Themes are OR within a
 * dimension and AND across them, and that rule lives in one place — the filter.
 */
export const selectedThemeSegments = (
    searchParams: URLSearchParams,
): string[] => {
    const segments = themeParams(searchParams).flatMap((key) =>
        (searchParams.get(key) ?? '').split(',').map((part) => part.trim()),
    );

    return [...new Set(segments.filter(Boolean))];
};

/** The themes in a dimension, in vocabulary order. */
export const groupThemesByDimension = (
    themes: ThemeFacetOption[],
): { dimension: string; themes: ThemeFacetOption[] }[] => {
    const groups: { dimension: string; themes: ThemeFacetOption[] }[] = [];

    for (const theme of themes) {
        const existing = groups.find(
            (group) => group.dimension === theme.dimension,
        );

        if (existing) {
            existing.themes.push(theme);
        } else {
            groups.push({ dimension: theme.dimension, themes: [theme] });
        }
    }

    return groups;
};

/**
 * One sidebar section per dimension.
 *
 * A dimension is a group heading rather than a prefix on a flat list because it
 * is what makes the vocabulary readable: "Material: Timber, Stone" is a choice
 * a reader can make, sixty undifferentiated pills is not.
 *
 * Pills on mobile while the vocabulary is short. A dimension holding more than
 * a pill group's worth wants the drill-in row that "Also in" uses.
 */
export const themeFilterSections = (
    themes: ThemeFacetOption[],
): ModalFiltersConfigSection[] =>
    groupThemesByDimension(themes).map((group) => ({
        title: group.dimension,
        collapsable: true,
        collapsedByDefault: false,
        mobileRefine: group.themes.length > 8 ? 'drillIn' : 'pills',
        filters: [
            {
                type: 'default' as const,
                filterKey: themeParamFor(group.dimension),
            },
        ],
    }));
